import { useEffect, useRef } from "react";

const SITE = "https://myfloorstudio.com.au";
const DEFAULT_OG_IMAGE = `${SITE}/logo/logo-primary.png`;
const JSONLD_ID_PREFIX = "mfs-jsonld-";

// Ensure a <meta> tag with the given attribute+value exists, then set its content.
function upsertMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Remove any previously injected per-route JSON-LD blocks so a route change
// swaps them cleanly (the base LocalBusiness/Organization JSON-LD in index.html
// is untouched — we only manage <script data-mfs-jsonld="…"> ourselves).
function clearRouteJsonLd() {
  document.head
    .querySelectorAll('script[data-mfs-jsonld]')
    .forEach((n) => n.parentNode.removeChild(n));
}

function addJsonLd(list) {
  if (!Array.isArray(list)) return;
  list.forEach((obj, i) => {
    if (!obj) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-mfs-jsonld", `${JSONLD_ID_PREFIX}${i}`);
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
  });
}

/**
 * useSEO — sets per-route <title>, description, canonical, Open Graph,
 * Twitter card, robots directive and (optionally) route-scoped JSON-LD.
 *
 * @param {object}   opts
 * @param {string}   opts.title
 * @param {string=}  opts.description
 * @param {string=}  opts.path         Path segment (default "/")
 * @param {string=}  opts.keywords     Comma-separated keywords
 * @param {string=}  opts.image        Absolute URL for OG image
 * @param {string=}  opts.imageAlt     Alt text for OG image
 * @param {string=}  opts.type         og:type (default "website")
 * @param {boolean=} opts.noindex      If true → robots="noindex, nofollow"
 * @param {object[]=}opts.jsonLd       Array of JSON-LD objects to inject
 */
export function useSEO({
  title,
  description,
  path = "/",
  keywords,
  image = DEFAULT_OG_IMAGE,
  imageAlt,
  type = "website",
  noindex = false,
  jsonLd,
} = {}) {
  // JSON-LD arrays are new identities on every render but their content is
  // stable, so the effect keys off the serialised form and reads the latest
  // value through a ref.
  const jsonLdKey = JSON.stringify(jsonLd || null);
  const jsonLdRef = useRef(jsonLd);
  jsonLdRef.current = jsonLd;

  useEffect(() => {
    if (title) document.title = title;
    const url = `${SITE}${path}`;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta(
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "My Floor Studio");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", imageAlt);
    upsertMeta("property", "og:locale", "en_AU");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertMeta("name", "twitter:image:alt", imageAlt);

    // Route-scoped JSON-LD
    clearRouteJsonLd();
    addJsonLd(jsonLdRef.current);

    return () => {
      // Only clear per-route JSON-LD on unmount so the next route can add
      // its own set. Meta/canonical will be overwritten by the next useSEO().
      clearRouteJsonLd();
    };
  }, [
    title,
    description,
    path,
    keywords,
    image,
    imageAlt,
    type,
    noindex,
    jsonLdKey,
  ]);
}

// ---------- Helper builders for common schema.org types ----------

export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

export function faqPage(qas) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qas.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  };
}

export { SITE };
